import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ContentGateRestriction } from "@ui/components/ContentGate";
import { useAuthHrefResolver } from "@ui/providers/AuthRoutingProvider";

import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { getUser } from "@jti/rest-api";

import { checkAgeVerified } from "../utils/checkAgeVerified";
import { checkLoggedIn } from "../utils/checkLoggedIn";
import { checkPurpose } from "../utils/checkPurpose";

export interface UseContentGateRestrictionsOptions {
  onRestrictionViolated?: (restriction: ContentGateRestriction) => void;
  onRestrictionPassed?: (restriction: ContentGateRestriction) => void;
}

export const useContentGateRestrictions = (
  restrictions: ContentGateRestriction[],
  options?: UseContentGateRestrictionsOptions
) => {
  const resolveAuthHref = useAuthHrefResolver();
  const [isEvaluating, setIsEvaluating] = useState(true);
  const [activeRestrictions, setActiveRestrictions] = useState<ContentGateRestriction[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const restrictionsKey = JSON.stringify(restrictions);
  const restrictionsRef = useRef(restrictions);
  if (JSON.stringify(restrictionsRef.current) !== restrictionsKey) {
    restrictionsRef.current = restrictions;
  }

  const checkRestrictions = useCallback(async () => {
    setIsEvaluating(true);
    const user = getUser();
    const active: ContentGateRestriction[] = [];
    const passed: ContentGateRestriction[] = [];
    const isLoggedIn = await checkLoggedIn(user);
    const isAgeVerified = await checkAgeVerified(user);

    const isDeviceRegistered = user?.is_device_registered || false;

    for (const restriction of restrictionsRef.current) {
      switch (restriction.type) {
        case "LOGGED_IN":
          if (!isLoggedIn) {
            active.push(restriction);
          } else {
            passed.push(restriction);
          }
          break;
        case "AGE_VERIFIED":
          if (!isAgeVerified) {
            active.push(restriction);
          } else {
            passed.push(restriction);
          }
          break;
        case "DEVICE_REGISTRATION":
          if (!isDeviceRegistered) {
            active.push(restriction);
          } else {
            passed.push(restriction);
          }
          break;
        case "PURPOSE": {
          const purposeResult = await checkPurpose(restriction.purposeId ?? "");
          if (!purposeResult) {
            active.push(restriction);
          } else {
            passed.push(restriction);
          }
          break;
        }
      }
    }

    if (options?.onRestrictionViolated) {
      active.forEach((restriction) => {
        options?.onRestrictionViolated?.(restriction);
      });
    }

    if (options?.onRestrictionPassed) {
      passed.forEach((restriction) => {
        options.onRestrictionPassed!(restriction);
      });
    }

    setActiveRestrictions(active);
    setIsDialogOpen(active.length > 0);
    setIsEvaluating(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictionsKey, options]);

  useEffect(() => {
    checkRestrictions();
  }, [checkRestrictions]);

  const modifiedActiveRestrictions = useMemo(() => {
    const currentPageUrl = window.location.href;
    const currentSearchParams = new URLSearchParams(window.location.search);
    const utmParams = new URLSearchParams();

    for (const [key, value] of currentSearchParams.entries()) {
      if (key.startsWith("utm_")) {
        utmParams.set(key, value);
      }
    }

    return activeRestrictions.map((restriction) => {
      if (restriction.type === "LOGGED_IN") {
        utmParams.set("returnUrl", currentPageUrl);
        const updatedLink = resolveAuthHref({
          route: AUTH_ROUTE_PATHS.login,
          search: utmParams,
        });

        return { ...restriction, link: updatedLink };
      }
      return restriction;
    });
  }, [activeRestrictions, resolveAuthHref]);

  return { activeRestrictions: modifiedActiveRestrictions, isDialogOpen, isEvaluating };
};
